import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="max-w-4xl mx-auto">
        <Button 
          onClick={() => navigate('/')}
          className="mb-6 bg-white/90 hover:bg-white text-[#4169E1] backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <Card className="bg-white/95 backdrop-blur-lg p-8 rounded-3xl shadow-2xl">
          <h1 className="text-4xl font-bold text-[#4169E1] mb-6 text-center">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8 text-center">Last Updated: November 2024</p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">1. Information We Collect</h2>
              <p className="mb-2">When you use Mega Church Game, we collect the following information:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Account Information:</strong> Email address, name, and password (encrypted)</li>
                <li><strong>Game Progress:</strong> Your level scores, total members, and game achievements</li>
                <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store credit card details)</li>
                <li><strong>Usage Data:</strong> Game session data, levels played, and gameplay statistics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">2. How We Use Your Information</h2>
              <p className="mb-2">We use your information to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and maintain the game experience</li>
                <li>Track your progress and save your game state</li>
                <li>Process premium level purchases</li>
                <li>Display leaderboard rankings</li>
                <li>Improve game features and user experience</li>
                <li>Send important updates about the game (if you opt-in)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">3. Data Storage and Security</h2>
              <p className="mb-2">
                Your data is stored securely using industry-standard encryption practices. We use MongoDB for data storage 
                and implement secure authentication tokens for session management. All payment processing is handled by 
                Stripe, a PCI-compliant payment processor, ensuring your financial information is never stored on our servers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">4. Third-Party Services</h2>
              <p className="mb-2">We use the following third-party services:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Google AdSense:</strong> To display advertisements (subject to Google's Privacy Policy)</li>
                <li><strong>Stripe:</strong> For secure payment processing (subject to Stripe's Privacy Policy)</li>
              </ul>
              <p className="mt-2">These services may collect their own data according to their respective privacy policies.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">5. Guest Mode</h2>
              <p>
                When playing in Guest Mode, we do not collect personal information. Your game progress is stored locally 
                on your device and is not synced to our servers. Guest progress is limited to Level 1 only.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">6. Data Sharing</h2>
              <p>
                We do not sell, trade, or rent your personal information to third parties. Your data is only used within 
                the game for the purposes outlined in this policy. Leaderboard rankings display only your name (or username) 
                and member count, not your email address.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">7. Your Rights</h2>
              <p className="mb-2">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your account and data (via Settings → Game Reset)</li>
                <li>Opt-out of promotional communications</li>
                <li>Export your game data upon request</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">8. Children's Privacy</h2>
              <p>
                Mega Church Game is a family-friendly Bible trivia game suitable for all ages. We do not knowingly collect 
                personal information from children under 13 without parental consent. If you believe your child has provided 
                us with personal information, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">9. Cookies and Tracking</h2>
              <p>
                We use session cookies to maintain your login state and improve your experience. You can disable cookies 
                in your browser settings, but this may affect game functionality. We also use Google AdSense, which may 
                use cookies for ad personalization.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by 
                updating the "Last Updated" date at the top of this policy. Continued use of the game after changes 
                constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#4169E1] mb-3">11. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact us 
                through the game's Settings page using the "Game Suggestion" feature, or reach out to our support team.
              </p>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-300">
              <p className="text-center text-sm text-gray-600">
                By using Mega Church Game, you acknowledge that you have read and understood this Privacy Policy 
                and agree to its terms.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
